import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { AdvertiserProvider } from './contexts/AdvertiserContext';
import ProtectedRoute from './components/ProtectedRoute';
import RequireFeature from './components/RequireFeature';
import Layout from './components/Layout';
import LoginPage from './components/LoginPage';
import Dashboard from './components/Dashboard';
import CampaignsPage from './components/CampaignsPage';
import EditCampaignPage from './components/EditCampaignPage';
import SenseMaxPage from './components/SenseMaxPage';
import SenseMaxDashboardPage from './components/SenseMaxDashboardPage';
import ReportsPage from './components/ReportsPage';
import URLListsPage from './components/URLListsPage';
import KeywordListsPage from './components/KeywordListsPage';
import TargetingTemplatesPage from './components/TargetingTemplatesPage';
import SuperGeoListsPage from './components/SuperGeoListsPage';
import CreativesPage from './components/CreativesPage';
import CreativeUploadPage from './components/CreativeUploadPage';
import EditCreativesPage from './components/EditCreativesPage';
import ConversionsPage from './components/ConversionsPage';
import RemarketingListsPage from './components/RemarketingListsPage';
import ResearchPage from './components/ResearchPage';
import ResearchSharePage from './components/ResearchSharePage';
import UsersPage from './components/UsersPage';
import RegionalOfficesPage from './components/RegionalOfficesPage';
import AdminPaymentProfilesPage from './components/AdminPaymentProfilesPage';
import DiscountTiersPage from './components/DiscountTiersPage';
import CommissionTiersPage from './components/CommissionTiersPage';
import AuditLogPage from './components/AuditLogPage';
import AgenciesPage from './components/AgenciesPage';
import ClientsPage from './components/ClientsPage';
import SettingsPage from './components/SettingsPage';
import BillingPage from './components/BillingPage';
import TransactionsPage from './components/TransactionsPage';
import CertificatesPage from './components/CertificatesPage';
import AcceptInvitePage from './components/AcceptInvitePage';
import RegisterPage from './components/RegisterPage';
import VerifyEmailPage from './components/VerifyEmailPage';
import './styles/reports.css';
import './styles/url-lists.css';
import './styles/conversions.css';
import './styles/research.css';
import './styles/login.css';

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
          <Route path="/auth/accept-invite" element={<AcceptInvitePage />} />

          {/* Share page without layout (for printing) - uses token instead of ID */}
          <Route path="/research/share/:token" element={<ResearchSharePage />} />

          {/* Protected routes with layout */}
          <Route path="/*" element={
            <ProtectedRoute>
              <AdvertiserProvider>
              <Layout>
                <Routes>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/campaigns" element={<CampaignsPage />} />
                  <Route path="/campaigns/new" element={
                    <ProtectedRoute excludeRoles={['analyst', 'agency_analyst']}>
                      <EditCampaignPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/campaigns/:campaignId/edit/:groupId?" element={
                    <ProtectedRoute excludeRoles={['analyst', 'agency_analyst']}>
                      <EditCampaignPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/sense-max" element={
                    <ProtectedRoute excludeRoles={['analyst', 'agency_analyst']}>
                      <SenseMaxPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/campaigns/:campaignId/dashboard" element={<SenseMaxDashboardPage />} />
                  <Route path="/reports" element={<ReportsPage />} />
                  <Route path="/url-lists" element={<URLListsPage />} />
                  <Route path="/keyword-lists" element={<KeywordListsPage />} />
                  <Route path="/targeting-templates" element={<TargetingTemplatesPage />} />
                  <Route path="/super-geo" element={<SuperGeoListsPage />} />
                  <Route path="/creatives" element={<RequireFeature feature="creatives"><CreativesPage /></RequireFeature>} />
                  <Route path="/creatives/upload" element={
                    <ProtectedRoute excludeRoles={['analyst', 'agency_analyst']}>
                      <CreativeUploadPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/creatives/:advertiserId/edit/:ids" element={
                    <ProtectedRoute excludeRoles={['analyst', 'agency_analyst']}>
                      <EditCreativesPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/conversions" element={<RequireFeature feature="conversions"><ConversionsPage /></RequireFeature>} />
                  <Route path="/remarketing-lists" element={<RequireFeature feature="remarketing"><RemarketingListsPage /></RequireFeature>} />
                  <Route path="/research" element={<RequireFeature feature="research"><ResearchPage /></RequireFeature>} />
                  <Route path="/settings" element={<SettingsPage />} />
                  {/* Finance routes */}
                  <Route path="/billing" element={
                    <RequireFeature feature="billing">
                      <ProtectedRoute requiredRoles={['admin', 'regional_admin', 'manager', 'agency_admin', 'agency_manager', 'agency_buyer', 'freelancer', 'smb_client']}>
                        <BillingPage />
                      </ProtectedRoute>
                    </RequireFeature>
                  } />
                  {/* Keep these role lists in step with the Finance section in
                      Layout — smb_client and agency_buyer see the links there,
                      and used to land on Access Denied. */}
                  <Route path="/transactions" element={
                    <ProtectedRoute requiredRoles={['admin', 'regional_admin', 'manager', 'agency_admin', 'agency_manager', 'agency_buyer', 'freelancer', 'smb_client', 'client']}>
                      <TransactionsPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/certificates" element={
                    <ProtectedRoute requiredRoles={['admin', 'regional_admin', 'manager', 'agency_admin', 'agency_manager', 'agency_buyer', 'freelancer', 'smb_client', 'client']}>
                      <CertificatesPage />
                    </ProtectedRoute>
                  } />
                  {/* Admin routes */}
                  <Route path="/users" element={
                    <ProtectedRoute requiredRoles={['admin', 'regional_admin', 'manager', 'agency_admin']}>
                      <UsersPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/clients" element={
                    <ProtectedRoute requiredRoles={['admin', 'regional_admin', 'manager', 'agency_admin', 'agency_manager', 'freelancer']}>
                      <ClientsPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/agencies" element={
                    <ProtectedRoute requiredRoles={['admin', 'regional_admin']}>
                      <AgenciesPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/regional-offices" element={
                    <ProtectedRoute requiredRoles={['admin']}>
                      <RegionalOfficesPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/payment-profiles" element={
                    <RequireFeature feature="payments">
                      <ProtectedRoute requiredRoles={['admin']}>
                        <AdminPaymentProfilesPage />
                      </ProtectedRoute>
                    </RequireFeature>
                  } />
                  <Route path="/discounts" element={
                    <RequireFeature feature="discounts">
                      <ProtectedRoute requiredRoles={['admin']}>
                        <DiscountTiersPage />
                      </ProtectedRoute>
                    </RequireFeature>
                  } />
                  <Route path="/commissions" element={
                    <ProtectedRoute requiredRoles={['admin']}>
                      <CommissionTiersPage />
                    </ProtectedRoute>
                  } />
                  <Route path="/audit-log" element={
                    <ProtectedRoute requiredRoles={['admin']}>
                      <AuditLogPage />
                    </ProtectedRoute>
                  } />
                </Routes>
              </Layout>
              </AdvertiserProvider>
            </ProtectedRoute>
          } />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
